/**
 * The monthly attendance report HR checks before payroll: one row per person
 * with the totals payroll will use, an Excel download in legacy's column
 * order, and a link to each person's printable timesheet.
 */
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Icon } from '../components/Icon.tsx';
import { Button, Callout, Chip, Empty, PageHead, Segmented, Skeleton, Tabs } from '../components/ui.tsx';
import { ApiError, get, qs } from '../lib/api.ts';
import { date, dateRange, minutes, todayIn } from '../lib/format.ts';
import { useLookups, useMe } from '../lib/session.ts';
import { hours, monthLabel, shiftMonth, type Basis, type ReportTotals } from './TimesheetPrint.tsx';

interface Row {
  employee: { id: string; name: string; employeeNo: string | null; department: string | null; division: string | null; section: string | null; position: string | null; level: string | null };
  totals: ReportTotals;
  absences: { date: string; note: string }[];
  lateEarly: { date: string; late: number; early: number }[];
  leaves: { date: string; code: string }[];
  overtime: { date: string; minutes: number }[];
  annualLeave: { remaining: number; periodFrom: string; periodTo: string };
}
interface Report {
  office: { id: string; name: string; timeZone: string };
  from: string;
  to: string;
  leaveTypes: { code: string; name: string }[];
  rows: Row[];
  summary: { people: number; present: number; absent: number; lateMinutes: number; earlyMinutes: number; leave: number; overtimeMinutes: number };
}

export const attendanceSearch = (s: Record<string, unknown>) => ({
  view: s.view === 'report' ? ('report' as const) : undefined,
  month: typeof s.month === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.month) ? s.month : undefined,
  basis: s.basis === 'calendar' || s.basis === 'payroll' ? (s.basis as Basis) : undefined,
  officeId: typeof s.officeId === 'string' && s.officeId ? s.officeId : undefined,
});

export function AttendanceTabs({ value }: { value: 'board' | 'report' }) {
  const navigate = useNavigate();
  return (
    <div style={{ marginBottom: 'var(--s-5)' }}>
      <Tabs
        idPrefix="attendance-view"
        value={value}
        onChange={(v) => void navigate({ to: '/attendance', search: v === 'report' ? { view: 'report' } : {} })}
        tabs={[
          { id: 'board', label: 'Live board' },
          { id: 'report', label: 'Monthly report' },
        ]}
      />
    </div>
  );
}

type SortKey = 'name' | 'workingDays' | 'present' | 'absent' | 'late' | 'early' | 'leave' | 'overtime' | 'annual';
const SORTERS: Record<SortKey, (r: Row) => number | string> = {
  name: (r) => r.employee.name,
  workingDays: (r) => r.totals.workingDays,
  present: (r) => r.totals.present,
  absent: (r) => r.totals.absent,
  late: (r) => r.totals.lateMinutes,
  early: (r) => r.totals.earlyMinutes,
  leave: (r) => r.totals.leave,
  overtime: (r) => r.totals.overtimeMinutes,
  annual: (r) => r.annualLeave.remaining,
};

export function AttendanceReport() {
  const me = useMe().data!;
  const lookups = useLookups();
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ReturnType<typeof attendanceSearch>;
  const month = search.month ?? todayIn(me.employee?.timeZone ?? undefined).slice(0, 7);
  const basis = search.basis ?? 'payroll';
  const officeId = search.officeId ?? me.employee?.officeId ?? lookups.data?.offices[0]?.id ?? '';
  const set = (next: { month?: string; basis?: Basis; officeId?: string }) => void navigate({ to: '/attendance', search: { view: 'report', month, basis, officeId: search.officeId, ...next }, replace: true });
  const params = { officeId, month, basis };
  const q = useQuery({ queryKey: ['attendance-report', officeId, month, basis], enabled: !!officeId, queryFn: () => get<Report>(`/api/attendance/reports/office${qs(params)}`) });
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: 'name', dir: 1 });
  const [filter, setFilter] = useState('');
  const r = q.data;

  const rows = useMemo(() => {
    const needle = filter.trim().toLowerCase();
    const list = (r?.rows ?? []).filter((x) => !needle || x.employee.name.toLowerCase().includes(needle) || (x.employee.employeeNo ?? '').includes(needle) || (x.employee.division ?? '').toLowerCase().includes(needle));
    const f = SORTERS[sort.key];
    return [...list].sort((a, b) => {
      const va = f(a);
      const vb = f(b);
      const c = typeof va === 'string' ? va.localeCompare(vb as string) : va - (vb as number);
      return c * sort.dir || a.employee.name.localeCompare(b.employee.name);
    });
  }, [r, filter, sort]);
  const upcoming = r ? Math.max(0, ...r.rows.map((x) => x.totals.upcoming)) : 0;

  const th = (k: SortKey, children: string, title?: string) => (
    <th key={k} className={k === 'name' ? undefined : 'right'} aria-sort={sort.key === k ? (sort.dir === 1 ? 'ascending' : 'descending') : 'none'} title={title}>
      <button type="button" className="th-sort" onClick={() => setSort((s) => (s.key === k ? { key: k, dir: s.dir === 1 ? -1 : 1 } : { key: k, dir: k === 'name' ? 1 : -1 }))}>
        {children}
        {sort.key === k ? <Icon name="chevronDown" width={12} height={12} style={{ transform: sort.dir === 1 ? 'rotate(180deg)' : undefined }} /> : null}
      </button>
    </th>
  );

  return (
    <div>
      <PageHead
        eyebrow={`Attendance report · ${r?.office.name ?? lookups.data?.offices.find((o) => o.id === officeId)?.name ?? '…'}`}
        title={
          <>
            {monthLabel(month)}, <em>in numbers.</em>
          </>
        }
        lede={
          r
            ? `${basis === 'payroll' ? 'Payroll period' : 'Calendar month'} ${dateRange(r.from, r.to)}. These are the counts payroll will use: correct a day on someone’s timesheet and this report follows.`
            : 'Per-person totals for the month: the counts payroll will use.'
        }
        aside={
          <>
            <div className="row" style={{ ['--gap' as string]: '4px' }}>
              <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous month" onClick={() => set({ month: shiftMonth(month, -1) })} />
              <span className="mono" style={{ minWidth: 64, textAlign: 'center' }}>
                {month}
              </span>
              <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next month" onClick={() => set({ month: shiftMonth(month, 1) })} />
            </div>
            <Button size="sm" icon="download" disabled={!r?.rows.length} onClick={() => window.location.assign(`/api/attendance/reports/office.xlsx${qs(params)}`)}>
              Excel
            </Button>
          </>
        }
      />
      <AttendanceTabs value="report" />
      <div className="toolbar">
        {(lookups.data?.offices.length ?? 0) > 1 ? (
          <select className="select" style={{ width: 'auto' }} value={officeId} onChange={(e) => set({ officeId: e.target.value })} aria-label="Office">
            {lookups.data?.offices.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        ) : null}
        <Segmented label="Period" idPrefix="report-basis" value={basis} onChange={(b) => set({ basis: b })} options={[{ id: 'payroll', label: 'Payroll period' }, { id: 'calendar', label: 'Calendar month' }]} />
        <input className="input" style={{ width: 220, height: 34 }} type="search" placeholder="Filter people" value={filter} onChange={(e) => setFilter(e.target.value)} aria-label="Filter people" />
        {upcoming ? (
          <span className="faint" style={{ marginLeft: 'auto', fontSize: 'var(--step--1)' }}>
            Period still running · {upcoming} work {upcoming === 1 ? 'day' : 'days'} to come
          </span>
        ) : null}
      </div>

      {r ? (
        <div className="ledger" style={{ ['--cols' as string]: 6, marginBottom: 'var(--s-6)' }}>
          <div className="ledger__cell">
            <div className="ledger__value">{r.summary.people}</div>
            <div className="ledger__label eyebrow">People</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value">{r.summary.present}</div>
            <div className="ledger__label eyebrow">Days present</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value" style={{ color: r.summary.absent ? 'var(--rose)' : undefined }}>
              {r.summary.absent}
            </div>
            <div className="ledger__label eyebrow">Days absent</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value" style={{ color: r.summary.lateMinutes ? 'var(--clay)' : undefined }}>
              {hours(r.summary.lateMinutes)}
              <small>h</small>
            </div>
            <div className="ledger__label eyebrow">Late in</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value">{r.summary.leave}</div>
            <div className="ledger__label eyebrow">Leave days</div>
          </div>
          <div className="ledger__cell">
            <div className="ledger__value">
              {hours(r.summary.overtimeMinutes)}
              <small>h</small>
            </div>
            <div className="ledger__label eyebrow">Overtime</div>
          </div>
        </div>
      ) : null}

      <div className="panel" style={{ padding: '4px 24px 8px' }}>
        {q.isLoading || (!officeId && lookups.isLoading) ? (
          <Skeleton h={360} />
        ) : q.error ? (
          <div style={{ padding: '16px 0' }}>
            <Callout tone="bad">{q.error instanceof ApiError ? q.error.message : 'The report could not be built.'}</Callout>
          </div>
        ) : !rows.length ? (
          <Empty title="Nobody here.">{filter ? 'No one matches this filter.' : 'No one in this office is in your scope for this month.'}</Empty>
        ) : (
          <div className="table-wrap">
            <table className="table report-table">
              <thead>
                <tr>
                  {th('name', 'Person')}
                  {th('workingDays', 'Work days', 'Scheduled work days in the period')}
                  {th('present', 'Present')}
                  {th('absent', 'Absent')}
                  {th('late', 'Late in')}
                  {th('early', 'Early out')}
                  {th('leave', 'Leave')}
                  {th('overtime', 'Overtime')}
                  {th('annual', 'AL left', 'Annual leave remaining in the leave year')}
                  <th aria-label="Timesheet" />
                </tr>
              </thead>
              <tbody>
                {rows.map((x) => {
                  const t = x.totals;
                  const print = { to: '/attendance/$employeeId/print' as const, params: { employeeId: x.employee.id }, search: { month, basis } };
                  return (
                    <tr key={x.employee.id}>
                      <td>
                        <Link {...print} className="link">
                          {x.employee.name}
                        </Link>
                        <div className="faint" style={{ fontSize: 'var(--step--2)' }}>
                          <span className="mono">{x.employee.employeeNo}</span>
                          {x.employee.division ? ` · ${x.employee.division}` : ''}
                        </div>
                      </td>
                      <td className="right num">{t.workingDays}</td>
                      <td className="right num">{t.present}</td>
                      <td className="right num" title={x.absences.map((a) => `${date(a.date, { year: false })}: ${a.note}`).join('\n') || undefined} style={{ color: t.absent ? 'var(--rose)' : undefined }}>
                        {t.absent}
                      </td>
                      <td className="right num" title={x.lateEarly.filter((l) => l.late).map((l) => `${date(l.date, { year: false })}: ${l.late} min`).join('\n') || undefined} style={{ color: t.lateMinutes ? 'var(--clay)' : undefined }}>
                        {t.lateMinutes ? minutes(t.lateMinutes) : '—'}
                        {t.lateDays ? <span className="faint"> · {t.lateDays}d</span> : null}
                      </td>
                      <td className="right num" title={x.lateEarly.filter((l) => l.early).map((l) => `${date(l.date, { year: false })}: ${l.early} min`).join('\n') || undefined}>
                        {t.earlyMinutes ? minutes(t.earlyMinutes) : '—'}
                        {t.earlyDays ? <span className="faint"> · {t.earlyDays}d</span> : null}
                      </td>
                      <td className="right">
                        {Object.entries(t.leaveByType).length ? (
                          <span className="row" style={{ justifyContent: 'flex-end', ['--gap' as string]: '4px' }}>
                            {Object.entries(t.leaveByType).map(([code, n]) => (
                              <Chip key={code}>
                                {code} {n}
                              </Chip>
                            ))}
                          </span>
                        ) : (
                          <span className="faint">—</span>
                        )}
                      </td>
                      <td className="right num">{t.overtimeMinutes ? `${hours(t.overtimeMinutes)} h` : <span className="faint">—</span>}</td>
                      <td className="right num">{x.annualLeave.remaining}</td>
                      <td className="right">
                        <Link {...print} className="btn btn--ghost btn--sm btn--icon" aria-label={`Printable timesheet for ${x.employee.name}`} title="Printable timesheet">
                          <Icon name="file" />
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
